import { useState, useSyncExternalStore } from "react";

/**
 * View state of sent messages' references (the "+N" expansion, the open details) held by the
 * conversation's attachment workspace, keyed by the message's references and each one's ID, so a
 * sent message remounting after a late refresh keeps it (#231).
 */
export class ReferenceUi {
  private readonly values = new Map<string, boolean>();
  private readonly listeners = new Set<() => void>();

  get(key: string) {
    return this.values.get(key) ?? false;
  }

  set(key: string, value: boolean) {
    if (this.get(key) === value) {
      return;
    }
    if (value) {
      this.values.set(key, true);
    } else {
      this.values.delete(key);
    }
    for (const listener of this.listeners) {
      listener();
    }
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

/** A flag of `ui` when there is one; component state otherwise (a message outside a workspace). */
export function useReferenceFlag(ui: ReferenceUi | undefined, key: string) {
  const [own, setOwn] = useState(false);
  const shared = useSyncExternalStore(ui?.subscribe ?? noSubscribe, () => ui?.get(key) ?? false);
  return ui
    ? ([shared, (value: boolean) => ui.set(key, value)] as const)
    : ([own, setOwn] as const);
}

const noSubscribe = () => () => {};
